import useSWR from "swr";
import { useMemo } from "react";

import { useServicesContext } from "../../providers/ServicesProvider";
import { DEFAULT_PAGE_SIZE, SWR_KEY_PREFIX } from "../../constants/constants";

/**
 * Lists an organization's bulk user CSV uploads.
 *
 * The upload list is filtered and paged server-side; `query` maps to the API's
 * `originalFileName` case-insensitive substring filter.
 *
 * Rows are created by the CSV import flow (see `useBulkUpload`, which fires
 * `EVENT_TYPES.ADD_BULK_UPLOAD`) and are then mutated server-side by the downstream processing
 * job as the status moves Uploaded → Processing → Completed. The app disables
 * `revalidateIfStale`/`revalidateOnFocus` globally (`App.tsx`), so this hook opts back into
 * revalidating on mount — otherwise a cached page keeps showing the state from the first visit
 * until a full page reload.
 */
export const useBulkUploads = ({
    organizationId,
    page = 1,
    query = "",
    size = DEFAULT_PAGE_SIZE,
    skip
}: {
    organizationId?: string;
    page?: number;
    query?: string;
    size?: number;
    skip?: boolean;
}) => {
    const { userService } = useServicesContext();

    const key = useMemo(() => {
        if (!organizationId || skip) return null;

        const searchParams = new URLSearchParams();
        searchParams.set("page", `${page}`);
        searchParams.set("size", `${size}`);
        if (query) {
            searchParams.set("originalFileName", query);
        }

        return `${
            SWR_KEY_PREFIX.BULK_UPLOADS
        }:/api/organizations/${organizationId}/users/bulk/uploads?${searchParams.toString()}`;
    }, [organizationId, page, query, size, skip]);

    const {
        data: uploads,
        isValidating,
        error
    } = useSWR(
        key,
        () =>
            userService.getBulkUploads({
                organizationId: organizationId ?? "",
                originalFileName: query,
                page,
                size
            }),
        { revalidateOnMount: true }
    );

    return {
        error,
        loading: isValidating,
        pageCount: uploads ? Math.ceil(uploads.totalCount / size) : 0,
        totalCount: uploads?.totalCount,
        uploads: uploads?.items || []
    };
};
